import { AppFullLocale, valueof } from '@/types';
import HipayService from '@/services/Hipay.service';
import { AjaxService } from '@/services/Ajax.service';
import { CartModel, CartPayModeModel } from '@/models';
import { AppDispatch } from '@/redux';
import { NoValidationService } from '@/services/NoValidation.service';
import { PayjunctionService } from '@/services/Payjunction.service';
import { RootState } from '@/redux/create-store';
import { ProviderInterface } from '@/services/ProviderInterface';
import { PAYMENT_PLATFORMS } from '@/constants/checkout';

type AdyenComponentRef = HTMLDivElement & {submit: () => void};
export interface PaymentProviderInterface {
  load(
    paymentProvider: valueof<typeof PAYMENT_PLATFORMS>,
    options?: {
      storeId: string, paymentMode: CartPayModeModel, onlySaveCard?: boolean,
      binding?: AdyenComponentRef, cartId?: string, getState?: () => RootState,
      translate?: (key: string) => string
    }
  ): Promise<Record<string, unknown> | void>
  initPaymentProvider(
    options: {locale: AppFullLocale, username: string, password: string},
    configuration?: Record<string, unknown>
  ): Promise<boolean>
  createPaymentProviderForm(
    paymentMode: CartPayModeModel, chosenPaymentProvider: valueof<typeof PAYMENT_PLATFORMS>,
    dispatch: AppDispatch, configuration?: unknown
  ): Promise<void>
  processPayment(cart: CartModel): Promise<void>
  validatePayment(
    cart: CartModel, optionalData: {details: unknown} | null
  ): Promise<Record<string, string>>
}

export class PaymentProvidersStrategy implements PaymentProviderInterface {
  constructor(private readonly ajaxService: AjaxService) {}
  private _dispatch: AppDispatch;
  private obj: ProviderInterface;

  set dispatch(dispatch: AppDispatch) {
    this._dispatch = dispatch;
  }

  private mapOfProviders = {
    [PAYMENT_PLATFORMS.ADYEN_APPLE_QUICK_PAY]: async () => {
      const ApplePayService = await import('@/services/ApplePay.service').then((module) => module.ApplePayService);
      return new ApplePayService(this.ajaxService);
    },
    [PAYMENT_PLATFORMS.ADYEN]: async () => {
      const AdyenService = await import('@/services/Adyen.service').then((module) => module.AdyenService);
      return new AdyenService(this.ajaxService);
    },
    [PAYMENT_PLATFORMS.ADYEN_APPLEPAY]: async () => {
      const AdyenService = await import('@/services/Adyen.service').then((module) => module.AdyenService);
      return new AdyenService(this.ajaxService);
    },
    [PAYMENT_PLATFORMS.ADYEN_TWINT]: async () => {
      const AdyenService = await import('@/services/Adyen.service').then((module) => module.AdyenService);
      return new AdyenService(this.ajaxService);
    },
    [PAYMENT_PLATFORMS.HIPAY]: () => new HipayService(this.ajaxService),
    [PAYMENT_PLATFORMS.PAY_JUNCTION]: () => new PayjunctionService(this.ajaxService),
    [PAYMENT_PLATFORMS.CSR_PAY_JUNCTION]: () => new PayjunctionService(this.ajaxService),
    [PAYMENT_PLATFORMS.NO_VALIDATION]: () => new NoValidationService(this.ajaxService),
    [PAYMENT_PLATFORMS.CART_ZERO]: () => new NoValidationService(this.ajaxService),
  };

  async load(
    paymentProvider: valueof<typeof PAYMENT_PLATFORMS>,
    options?: {
      storeId: string, paymentMode: CartPayModeModel, onlySaveCard: boolean,
      binding?: AdyenComponentRef, cartId?: string, getState?: () => RootState,
      translate?: (key: string) => string
    }
  ): Promise<Record<string, unknown> | void> {
    this.obj = await this.mapOfProviders[paymentProvider]();
    return await this.obj.load(options);
  }

  async initPaymentProvider(options: {locale: AppFullLocale, username: string, password: string},
    configuration: Record<string, unknown>
  ): Promise<boolean> {
    return await this.obj.initPaymentProvider(options, configuration);
  }

  async createPaymentProviderForm(
    paymentMode: CartPayModeModel
  ): Promise<void> {
    return await this.obj.createPaymentProviderForm(this._dispatch, HIPAY_FORM_BINDING, paymentMode);
  }

  async processPayment(cart: CartModel): Promise<void> {
    return await this.obj.processPayment(cart);
  }

  async validatePayment(
    cart: CartModel,
    optionalData: {
      details?: unknown, transactionId?: string, provider?: valueof<typeof PAYMENT_PLATFORMS>,
      dispatch?: AppDispatch
    } | null = null
  ) {
    if (optionalData?.provider) {
      this.obj = await this.mapOfProviders[optionalData.provider]();
      this.obj._storeId = cart.storeId;
    }
    this.obj._dispatch = optionalData.dispatch || this._dispatch;
    return await this.obj.validatePayment(cart, optionalData);
  }
}

const placeholderHyphens = '- - - -';
const doubleSpace = '  ';

const HIPAY_FORM_BINDING = {
  cardHolder: {
    selector: 'hipay-card-holder',
    placeholder: ''
  },
  cardNumber: {
    selector: 'hipay-card-number',
    placeholder: Array(4).fill(placeholderHyphens).join(doubleSpace)
  },
  expiryDate: {
    selector: 'hipay-expiry-date'
  },
  cvc: 'hipay-cvc'
};
